import React, { useEffect, useState } from 'react';

import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MeasuringStrategy,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import PropTypes from 'prop-types';

import DraggableOverlay from './DraggableOverlay';
import ItemContent from './ItemContent';
import ItemsStyles from './Items.module.css';
import SortableItem from './SortableItem';

const measuring = {
    droppable: {
        strategy: MeasuringStrategy.Always,
    },
};

const Container = ({
    canModify,
    onDeletePage,
    onDuplicatePage,
    pages,
    pageSize,
    selectedPageId,
    selectPage,
    updateOrder,
}) => {
    const [activeId, setActiveId] = useState(null);
    const [items, setItems] = useState([]);
    const activeIndex =
        activeId != null ? items.findIndex((item) => item.id === activeId) : -1;
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: { distance: 10 },
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        }),
    );

    function handleDragStart({ active }) {
        setActiveId(active.id);
    }

    function handleDragCancel() {
        setActiveId(null);
    }

    function handleDragEnd({ over }) {
        if (over) {
            const overIndex = items.findIndex((item) => item.id === over.id);

            if (activeIndex !== overIndex) {
                const newIndex = overIndex;

                setItems((items) => arrayMove(items, activeIndex, newIndex));

                updateOrder(activeIndex, newIndex);
            }
        }

        setActiveId(null);
    }

    useEffect(() => {
        setItems(pages);
    }, [pages]);

    return (
        <DndContext
            collisionDetection={closestCenter}
            measuring={measuring}
            onDragCancel={handleDragCancel}
            onDragEnd={handleDragEnd}
            onDragStart={handleDragStart}
            sensors={sensors}
        >
            <SortableContext items={items}>
                <ul className={ItemsStyles.Items}>
                    {items.map((item) => (
                        <SortableItem
                            activeIndex={activeIndex}
                            id={item.id}
                            key={item.id}
                        >
                            <ItemContent
                                canModify={canModify}
                                index={item.page}
                                item={item}
                                onDeletePage={onDeletePage}
                                onDuplicatePage={onDuplicatePage}
                                pageSize={pageSize}
                                selectPage={selectPage}
                                selectedPageId={selectedPageId}
                            />
                        </SortableItem>
                    ))}
                </ul>
            </SortableContext>

            <DraggableOverlay
                renderer={(props) => {
                    const item = items.find((item) => item.id === activeId);
                    return (
                        <ItemContent
                            canModify={canModify}
                            handleClickSelect={selectPage}
                            index={item.page}
                            item={item}
                            onDeletePage={onDeletePage}
                            onDuplicatePage={onDuplicatePage}
                            pageSize={pageSize}
                            selectedPageId={selectedPageId}
                            {...props}
                        />
                    );
                }}
            ></DraggableOverlay>
        </DndContext>
    );
};

Container.propTypes = {
    canModify: PropTypes.bool,
    onDeletePage: PropTypes.func,
    onDuplicatePage: PropTypes.func,
    pages: PropTypes.array,
    pageSize: PropTypes.string,
    selectedPageId: PropTypes.string,
    selectPage: PropTypes.func,
    updateOrder: PropTypes.func,
};

export default Container;
